import { ReactElement, useEffect } from "react";
import classNames from "classnames";
import { withoutLocaleInURI } from "../../internationalization/i18nURI";
import URI from "urijs";
import ClickableLink from "../utilities/ClickableLink";
import styles from "./styles/UserDropDownMenu.module.scss";
import { observer } from "mobx-react";
import { LogoutButton } from "../admin/sidebar/items/login/LogoutButton";
import {
  isSchoolBlocksApp,
  isSchoolFeedStandalone,
} from "../../utils/SchoolBlocksUtilities";
import useClickOutside from "../../hooks/useClickOutside";
import { buildCssUrlString } from "../../utils/StringUtilities";
import { NotificationsIcon } from "./NotificationsIcon";
import { useStores } from "../../stores/useStores";
import { useFeatureFlags } from "../../utils/featureFlags/useFeatureFlags";

export interface UserMenuDropDownItem {
  onClick?: () => void;
  path?: string;
  title: string | ReactElement;
  icon: string;
}

export function useUserMenuLinks(
  additionalFields: UserMenuDropDownItem[] = [],
): UserMenuDropDownItem[] {
  const { userStore, modalStore } = useStores();
  const isSchoolBlocks = isSchoolBlocksApp();
  const { show_two_way_messaging } = useFeatureFlags();

  if (!userStore.id) {
    return [
      {
        icon: "fas fa-sign-in-alt",
        title: isSchoolFeedStandalone()
          ? "Login to Join the Discussion"
          : "Login",
        onClick: () => {
          if (document.activeElement) {
            // prevents weird behavior on modal close when modal returns focus to activeElement
            (document.activeElement as HTMLElement).blur();
          }
          modalStore.addModal({ type: "login" });
        },
      },
    ];
  } else {
    return [
      ...[
        {
          onClick: () => {
            if (document.activeElement) {
              // prevents weird behavior on modal close when modal returns focus to activeElement
              (document.activeElement as HTMLElement).blur();
            }
            modalStore.addModal({ type: "activity" });
          },
          title: (
            <>
              My Activity <NotificationsIcon />
            </>
          ),
          icon: "fa-regular fa-bell",
        },
      ],
      ...(show_two_way_messaging
        ? [
            {
              path: "/cms/user/messaging",
              title: "Messaging",
              icon: "fa-regular fa-message",
            },
          ]
        : []),
      ...(isSchoolBlocks
        ? [
            {
              path: "/cms/user/settings",
              title: "My Settings",
              icon: "fas fa-cog",
            },
          ]
        : []),
      ...(userStore.isEditor
        ? [
            {
              path: new URI(withoutLocaleInURI("/dashboard")).toString(),
              title: "My Dashboard",
              icon: "fas fa-pencil",
            },
          ]
        : []),
      ...(isSchoolBlocks && userStore.isAdmin
        ? [
            {
              path: "/cms/admin/organizationSettings",
              title: "Configuration",
              icon: "fas fa-cog",
            },
          ]
        : []),

      ...additionalFields,
    ];
  }
}

const UserMenuDropDown = observer(
  (props: {
    preventTabbing?: boolean;
    customLoginText?: string;
    buttonClassName?: string;
    menuClassName?: string;
  }) => {
    const { interfaceStore, userStore, modalStore } = useStores();

    const links = useUserMenuLinks();

    function onClick() {
      if (interfaceStore.userMenuOpen) {
        interfaceStore.toggleUserMenu();
      }
    }

    const dropdownRef = useClickOutside(onClick);

    const dropDownContainerClassName = classNames({
      [styles.container]: true,
      [styles.containerOpen]: interfaceStore.userMenuOpen,
      [props.menuClassName || ""]: props.menuClassName,
    });

    const menuButtonClassName = classNames({
      btn: true,
      "sb-organization-color-element-bg": true,
      [styles.userMenuButton]: true,
      [props.buttonClassName || ""]: props.buttonClassName,
    });

    const focusCloseButton = () => {
      const closeButton = document.getElementById(
        `userMenuButton`,
      ) as HTMLButtonElement;
      if (closeButton) closeButton.focus();
    };

    const focusFirstButton = () => {
      const firstButton = document.getElementById(
        "userMenu-firstbutton",
      ) as HTMLButtonElement;
      if (firstButton) firstButton.focus();
    };

    const handleKeydown = (evt) => {
      if (evt.code === "Escape") {
        focusCloseButton();
        interfaceStore.toggleUserMenu();
      }
    };
    useEffect(() => {
      if (interfaceStore.userMenuOpen) {
        window.addEventListener("keydown", handleKeydown);
        return () => {
          window.removeEventListener("keydown", handleKeydown);
        };
      }
    }, [interfaceStore.userMenuOpen]);

    return (
      <div ref={dropdownRef} className={styles.userMenu}>
        {userStore.id ? (
          <>
            <button
              id="userMenuButton"
              onClick={() => interfaceStore.toggleUserMenu()}
              type="button"
              className={menuButtonClassName}
              style={{ whiteSpace: "nowrap" }}
              aria-label="Open menu drawer"
              tabIndex={props.preventTabbing ? -1 : 0}
            >
              {userStore.avatar && (
                <div
                  className="sb-navbar-button-image"
                  style={{
                    backgroundImage: buildCssUrlString(userStore.avatar),
                  }}
                />
              )}
              <div className="sb-navbar-button-text" aria-hidden="true">
                My Account
              </div>
              {userStore.id && (
                <NotificationsIcon className={styles.notificationIcon} />
              )}
            </button>
            <div className={dropDownContainerClassName}>
              <ul>
                {links.map((link, i) => {
                  const isCurrentPath =
                    URI(interfaceStore.currentFullUrl).path() === link.path;

                  return (
                    <li key={i}>
                      {link.onClick ? (
                        <button
                          onClick={() => {
                            onClick();
                            if (link.onClick) {
                              link.onClick();
                            }
                          }}
                          className={`${isCurrentPath ? "sb-organization-color-element-outline" : ""}`}
                          id={i === 0 ? "userMenu-firstbutton" : undefined}
                          tabIndex={interfaceStore.userMenuOpen ? 0 : -1}
                        >
                          <div>
                            <span
                              className={classNames(link.icon, styles.icon)}
                            />
                          </div>
                          <span>{link.title}</span>
                        </button>
                      ) : (
                        <ClickableLink
                          href={link.path}
                          id={i === 0 ? "userMenu-firstbutton" : undefined}
                          tabIndex={interfaceStore.userMenuOpen ? 0 : -1}
                          type="button"
                          onClick={onClick}
                          className={`${isCurrentPath ? "sb-organization-color-element-outline" : ""}`}
                        >
                          <div>
                            <span
                              className={classNames(link.icon, styles.icon)}
                            />
                          </div>
                          <span>{link.title}</span>
                        </ClickableLink>
                      )}
                    </li>
                  );
                })}
                <li>
                  <LogoutButton tabIndex={interfaceStore.userMenuOpen ? 0 : -1}>
                    <div>
                      <span
                        className={classNames("fas fa-times", styles.icon)}
                      />
                    </div>
                    <span>Logout</span>
                  </LogoutButton>
                </li>
                <span
                  tabIndex={interfaceStore.userMenuOpen ? 0 : -1}
                  onFocus={focusFirstButton}
                />
              </ul>
            </div>
          </>
        ) : (
          <div>
            <button
              className={menuButtonClassName}
              style={{ minWidth: "unset" }}
              aria-label={"Open Login Modal"}
              id="loginMenuButton"
              onClick={() => {
                modalStore.addModal({
                  type: "login",
                  size: "lg",
                  id: "login-modal",
                });
              }}
            >
              <span className={"sb-navbar-button-text"}>
                {props.customLoginText || "Login"}
              </span>
            </button>
          </div>
        )}
      </div>
    );
  },
);
export default UserMenuDropDown;
